'use client';

import { AuthApi } from '@up2stay/up2promo-api-client';
import { Alert, AlertDescription } from '@up2stay/up2promo-ui';
import { Button } from '@up2stay/up2promo-ui';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@up2stay/up2promo-ui';
import { Input } from '@up2stay/up2promo-ui';
import { Label } from '@up2stay/up2promo-ui';
import { PhoneInput } from '@up2stay/up2promo-ui';
import { BadgeCheck, Trash2 } from 'lucide-react';
import { useState } from 'react';
import de from 'react-phone-number-input/locale/de';
import en from 'react-phone-number-input/locale/en';
import { toast } from 'sonner';

import { SettingsSection } from '@/core/components/settings/settings-section';
import { useLocale } from '@/core/hooks/use-locale';
import { getApiValidationErrorCode } from '@/core/utils/api-error.utils';

enum PhoneStep {
  DISPLAY = 'display',
  ENTER_PHONE = 'enter-phone',
  ENTER_CODE = 'enter-code',
}

interface PhoneDict {
  title: string;
  description: string;
  noPhone: string;
  addPhone: string;
  changePhone: string;
  removePhone: string;
  phoneLabel: string;
  phonePlaceholder: string;
  phoneFormatHint: string;
  sendCode: string;
  resendCode: string;
  verificationCodeLabel: string;
  verificationCodeSent: string;
  verified: string;
  removed: string;
  unavailable: string;
  errors: {
    invalidPhoneNumber: string;
    rateLimited: string;
    smsSendFailed: string;
    smsUnavailable: string;
    expired: string;
    tooManyAttempts: string;
    invalid: string;
    verificationFailed: string;
    removeFailed: string;
    generic: string;
  };
}

interface PhoneSectionProps {
  sectionId: string;
  phone: string | null | undefined;
  phoneVerified: boolean | undefined;
  onPhoneChanged: () => void;
  dict: PhoneDict;
  settingsDict: {
    cancel: string;
  };
  smsVerificationAvailable?: boolean;
}

export function PhoneSection({
  sectionId,
  phone,
  phoneVerified,
  onPhoneChanged,
  dict,
  settingsDict,
  smsVerificationAvailable = true,
}: PhoneSectionProps) {
  const locale = useLocale();
  const phoneLabels = locale === 'de' ? de : en;
  const [step, setStep] = useState<PhoneStep>(PhoneStep.DISPLAY);
  const [phoneInput, setPhoneInput] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleStartAdd = () => {
    setStep(PhoneStep.ENTER_PHONE);
    setPhoneInput(phone ?? '');
    setError(null);
  };

  const handleCancel = () => {
    setStep(PhoneStep.DISPLAY);
    setPhoneInput('');
    setOtpCode('');
    setError(null);
  };

  const handleRequestCode = async () => {
    if (!phoneInput.trim()) return;
    setIsLoading(true);
    setError(null);

    try {
      await AuthApi.requestPhoneVerification({
        body: { phone: phoneInput.trim() },
      });
      setStep(PhoneStep.ENTER_CODE);
      setOtpCode('');
    } catch (err: unknown) {
      const errorCode = getApiValidationErrorCode(err, 'phone') ?? 'unknown';
      const errorMap: Record<string, string> = {
        invalidPhoneNumber: dict.errors.invalidPhoneNumber,
        rateLimited: dict.errors.rateLimited,
        smsSendFailed: dict.errors.smsSendFailed,
        smsUnavailable: dict.errors.smsUnavailable,
      };
      setError(errorMap[errorCode] ?? dict.errors.generic);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify = async (code: string) => {
    if (!/^\d{6}$/.test(code)) return;
    setIsLoading(true);
    setError(null);

    try {
      await AuthApi.verifyPhone({
        body: { code },
      });
      toast.success(dict.verified);
      setStep(PhoneStep.DISPLAY);
      setOtpCode('');
      onPhoneChanged();
    } catch (err: unknown) {
      const errorCode = getApiValidationErrorCode(err, 'code') ?? 'unknown';
      const errorMap: Record<string, string> = {
        expired: dict.errors.expired,
        tooManyAttempts: dict.errors.tooManyAttempts,
        invalid: dict.errors.invalid,
      };
      setError(errorMap[errorCode] ?? dict.errors.verificationFailed);
      // Empty field so the next code (typed or iOS autofill) is not mixed
      // with the rejected one.
      setOtpCode('');
      if (errorCode === 'tooManyAttempts') {
        setStep(PhoneStep.ENTER_PHONE);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemove = async () => {
    setIsLoading(true);
    setError(null);

    try {
      await AuthApi.removePhone();
      toast.success(dict.removed);
      onPhoneChanged();
    } catch {
      setError(dict.errors.removeFailed);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <SettingsSection
      id={sectionId}
      title={dict.title}
      footer={dict.description}
      grouped
    >
      <div className="space-y-4 px-4 py-3.5 sm:px-5">
        {error ? (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}

        {step === PhoneStep.DISPLAY ? (
          <div className="space-y-4">
            {phone && phoneVerified ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Input
                    value={phone}
                    readOnly
                    aria-label={dict.phoneLabel}
                    className="bg-muted"
                  />
                  <BadgeCheck className="h-5 w-5 shrink-0 text-emerald-600" />
                </div>
                <div className="flex gap-2">
                  {smsVerificationAvailable ? (
                    <Button variant="outline" onClick={handleStartAdd}>
                      {dict.changePhone}
                    </Button>
                  ) : null}
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => void handleRemove()}
                    disabled={isLoading}
                    aria-label={dict.removePhone}
                    title={dict.removePhone}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ) : smsVerificationAvailable ? (
              <div className="space-y-3">
                <p className="text-[13.5px] text-muted-foreground">
                  {dict.noPhone}
                </p>
                <Button variant="outline" onClick={handleStartAdd}>
                  {dict.addPhone}
                </Button>
              </div>
            ) : (
              <p className="text-[12px] leading-snug text-muted-foreground/85">
                {dict.unavailable}
              </p>
            )}
          </div>
        ) : null}

        {step === PhoneStep.ENTER_PHONE ? (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{dict.phoneLabel}</Label>
              <PhoneInput
                defaultCountry="DE"
                labels={phoneLabels}
                placeholder={dict.phonePlaceholder}
                value={phoneInput || undefined}
                onChange={(v) => setPhoneInput(v || '')}
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleCancel}>
                {settingsDict.cancel}
              </Button>
              <Button
                onClick={() => void handleRequestCode()}
                disabled={!phoneInput.trim() || isLoading}
                isLoading={isLoading}
              >
                {dict.sendCode}
              </Button>
            </div>
          </div>
        ) : null}

        {step === PhoneStep.ENTER_CODE ? (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{dict.verificationCodeLabel}</Label>
              <p className="text-[12px] leading-snug text-muted-foreground/85">
                {dict.verificationCodeSent.replace('{phone}', phoneInput)}
              </p>
              <InputOTP
                maxLength={6}
                value={otpCode}
                autoComplete="one-time-code"
                inputMode="numeric"
                autoFocus
                onChange={(value) => {
                  setOtpCode(value);
                  if (/^\d{6}$/.test(value)) {
                    void handleVerify(value);
                  }
                }}
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={handleCancel}>
                {settingsDict.cancel}
              </Button>
              <Button
                variant="ghost"
                className="text-sm"
                onClick={() => void handleRequestCode()}
                disabled={isLoading}
              >
                {dict.resendCode}
              </Button>
            </div>
          </div>
        ) : null}
      </div>
    </SettingsSection>
  );
}
